import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, getDoc, doc, Timestamp } from 'firebase/firestore';
import { db as firebaseDb } from '../firebase';
import { Clock, Calendar } from 'lucide-react';

const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

const DAY_HEADERS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const EmployeeMonthView = ({ db = firebaseDb, employee, currentDate, hasPermission, onDayClick }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);

  // Build the month grid (Mon–Sun rows)
  const getMonthGrid = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Start from Monday of the first week
    const startDay = new Date(firstDay);
    const dow = firstDay.getDay(); // 0=Sun
    startDay.setDate(firstDay.getDate() - (dow === 0 ? 6 : dow - 1));

    // End on Sunday of the last week
    const endDay = new Date(lastDay);
    const dowEnd = lastDay.getDay();
    if (dowEnd !== 0) endDay.setDate(lastDay.getDate() + (7 - dowEnd));

    const days = [];
    const cursor = new Date(startDay);
    while (cursor <= endDay) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [currentDate]);

  const fetchAppointments = useCallback(async () => {
    if (!db || !employee?.id) return;
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      end.setHours(23, 59, 59, 999);

      const q = query(
        collection(db, 'appointments'),
        where('employeeId', '==', employee.id),
        where('date', '>=', Timestamp.fromDate(start)),
        where('date', '<=', Timestamp.fromDate(end))
      );
      const snap = await getDocs(q);
      const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Enrichir avec les données client (nom + couleur)
      const clientCache = new Map();
      const enriched = await Promise.all(raw.map(async apt => {
        if (!apt.clientId) return apt;
        if (!clientCache.has(apt.clientId)) {
          const clientSnap = await getDoc(doc(db, 'clients', apt.clientId));
          clientCache.set(apt.clientId, clientSnap.exists() ? clientSnap.data() : null);
        }
        const clientData = clientCache.get(apt.clientId);
        return {
          ...apt,
          clientName: clientData ? `${clientData.lastName || ''} ${clientData.firstName || ''}`.trim() : 'Bénéficiaire inconnu',
          clientColor: clientData?.color || '#3b82f6',
        };
      }));
      setAppointments(enriched);
    } catch (e) {
      console.error('EmployeeMonthView: fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [db, employee?.id, currentDate]);

  useEffect(() => {
    fetchAppointments();
    setExpandedDay(null);
  }, [fetchAppointments]);

  const getAptsForDay = (date) =>
    appointments
      .filter(apt => {
        const d = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);
        return isSameDay(d, date);
      })
      .sort((a, b) => {
        const [ah, am] = (a.startTime || '0:0').split(':').map(Number);
        const [bh, bm] = (b.startTime || '0:0').split(':').map(Number);
        return (ah * 60 + am) - (bh * 60 + bm);
      });

  // Monthly totals
  const totalMin = appointments.reduce((acc, apt) => {
    const [sh, sm] = (apt.startTime || '0:0').split(':').map(Number);
    const [eh, em] = (apt.endTime || '0:0').split(':').map(Number);
    return acc + ((eh * 60 + em) - (sh * 60 + sm));
  }, 0);
  const totalLabel = `${Math.floor(totalMin / 60)}h${totalMin % 60 > 0 ? String(totalMin % 60).padStart(2, '0') : ''}`;

  const today = new Date();
  const currentMonth = currentDate.getMonth();
  const days = getMonthGrid();

  const MAX_VISIBLE = 3;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Summary bar */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4 text-sm flex-shrink-0">
        <span className="text-gray-600 dark:text-gray-400">
          <strong className="text-gray-900 dark:text-gray-100">{appointments.length}</strong> intervention{appointments.length > 1 ? 's' : ''} ce mois
        </span>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        <span className="text-gray-600 dark:text-gray-400">
          <strong className="text-gray-900 dark:text-gray-100">{totalLabel}</strong> au total
        </span>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        <span className="text-gray-600 dark:text-gray-400">
          <strong className="text-gray-900 dark:text-gray-100">
            {new Set(appointments.map(a => a.clientId).filter(Boolean)).size}
          </strong> bénéficiaires
        </span>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-50 dark:bg-gray-900">
        {DAY_HEADERS.map(d => (
          <div key={d} className="py-2 text-center text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-7 divide-x divide-gray-100 dark:divide-gray-800">
          {days.map((day, idx) => {
            const isCurrentMonth = day.getMonth() === currentMonth;
            const isToday = isSameDay(day, today);
            const dayApts = getAptsForDay(day);
            const dayKey = day.toDateString();
            const isExpanded = expandedDay === dayKey;
            const visibleApts = isExpanded ? dayApts : dayApts.slice(0, MAX_VISIBLE);
            const hiddenCount = dayApts.length - MAX_VISIBLE;

            return (
              <div
                key={idx}
                className={`min-h-[100px] md:min-h-[120px] border-b border-gray-100 dark:border-gray-800 p-1 flex flex-col ${
                  isCurrentMonth ? '' : 'bg-gray-50/50 dark:bg-gray-900/50'
                } ${isToday ? 'bg-blue-50/60 dark:bg-blue-900/10' : ''}`}
              >
                {/* Day number */}
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-xs md:text-sm font-semibold w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${
                      isToday
                        ? 'bg-blue-600 text-white'
                        : isCurrentMonth
                        ? 'text-gray-800 dark:text-gray-200'
                        : 'text-gray-300 dark:text-gray-600'
                    }`}
                  >
                    {day.getDate()}
                  </span>
                  {dayApts.length > 0 && isCurrentMonth && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                      {(() => {
                        const min = dayApts.reduce((acc, apt) => {
                          const [sh, sm] = (apt.startTime || '0:0').split(':').map(Number);
                          const [eh, em] = (apt.endTime || '0:0').split(':').map(Number);
                          return acc + ((eh * 60 + em) - (sh * 60 + sm));
                        }, 0);
                        return `${Math.floor(min / 60)}h${min % 60 > 0 ? String(min % 60).padStart(2, '0') : ''}`;
                      })()}
                    </span>
                  )}
                </div>

                {/* Appointments */}
                <div className="flex-1 space-y-0.5 overflow-hidden">
                  {visibleApts.map(apt => {
                    const aptDate = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);
                    const [sh, sm] = (apt.startTime || '0:0').split(':').map(Number);
                    const [eh, em] = (apt.endTime || '0:0').split(':').map(Number);
                    const start = new Date(aptDate); start.setHours(sh, sm, 0, 0);
                    const end = new Date(aptDate); end.setHours(eh, em, 0, 0);
                    const isCurrent = start <= today && end >= today && isSameDay(aptDate, today);
                    const clientColor = apt.clientColor || '#3b82f6';

                    return (
                      <div
                        key={apt.id}
                        className={`rounded px-1 py-0.5 text-[10px] md:text-xs truncate leading-tight cursor-default hover:opacity-80 transition-opacity ${isCurrent ? 'ring-1 ring-amber-400' : ''}`}
                        style={{
                          backgroundColor: `${clientColor}22`,
                          borderLeft: `2px solid ${clientColor}`,
                          color: 'inherit'
                        }}
                        title={`${apt.clientName || '—'} · ${apt.startTime}–${apt.endTime}`}
                      >
                        <span className="font-medium text-gray-800 dark:text-gray-200 truncate block">
                          {apt.startTime} {apt.clientName || '—'}
                        </span>
                      </div>
                    );
                  })}

                  {/* Show more / less toggle */}
                  {!isExpanded && hiddenCount > 0 && (
                    <button
                      onClick={() => setExpandedDay(dayKey)}
                      className="text-[10px] text-blue-500 dark:text-blue-400 hover:underline font-medium pl-1"
                    >
                      +{hiddenCount} autre{hiddenCount > 1 ? 's' : ''}
                    </button>
                  )}
                  {isExpanded && dayApts.length > MAX_VISIBLE && (
                    <button
                      onClick={() => setExpandedDay(null)}
                      className="text-[10px] text-gray-400 dark:text-gray-500 hover:underline font-medium pl-1"
                    >
                      Réduire
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EmployeeMonthView;
